import { useForm } from "react-hook-form";
import { Loader2Icon } from "lucide-react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  DialogFooter,
  Form,
  FormField,
  FormSelect,
  FormToggleGroup,
  useMultiStepForm,
} from "web-design-system";
import { useUpdateInvestorProfileDetails } from "creso-core/hooks";
import {
  INCOME_RANGE,
  OCCUPATION,
  PEP_STATUS,
  SOURCE_OF_WEALTH,
} from "creso-core/constants";
import {
  IncomeSlab,
  Occupation,
  OtherInfoSchema,
  PepStatus,
  SourceOfWealth,
} from "creso-core/zod";
import { handleError } from "../../utils";
import { useClientOnboarding } from "./context";
import { useMinorOnboarding } from "./minor-form-data";

/** Minor onboarding, new guardian only: the guardian's financial profile */
export function GuardianProfile() {
  const { previousStep, nextStep } = useMultiStepForm();
  const { client } = useClientOnboarding();
  const { guardian } = useMinorOnboarding();
  const { mutateAsync: updateProfile } = useUpdateInvestorProfileDetails();
  const saved = guardian?.profile;

  const form = useForm<z.infer<typeof OtherInfoSchema>>({
    resolver: zodResolver(OtherInfoSchema),
    defaultValues: {
      occupation: (saved?.occupation ?? OCCUPATION[0].value) as Occupation,
      income_slab: (saved?.income_slab ?? INCOME_RANGE[0].value) as IncomeSlab,
      source_of_wealth: (saved?.source_of_wealth ??
        SOURCE_OF_WEALTH[0].value) as SourceOfWealth,
      pep_details: (saved?.pep_details ?? PEP_STATUS[0].value) as PepStatus,
    },
  });

  const onSubmit = async (data: z.infer<typeof OtherInfoSchema>) => {
    if (!client) return;
    try {
      await updateProfile({
        client_id: client.id,
        holder_role: "guardian",
        ...data,
      });
      nextStep();
    } catch (error) {
      handleError(error, "Failed to save guardian profile");
    }
  };

  return (
    <div className="flex flex-col size-full p-6 gap-6">
      <span className="text-lg font-semibold">Guardian profile</span>
      <Form {...form}>
        <form
          className="flex flex-col flex-1 gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="occupation"
            render={({ field }) => (
              <FormSelect label="Occupation" options={OCCUPATION} {...field} />
            )}
          />
          <FormField
            control={form.control}
            name="income_slab"
            render={({ field }) => (
              <FormToggleGroup
                options={INCOME_RANGE}
                {...field}
                label="Income range"
              />
            )}
          />
          <FormField
            control={form.control}
            name="source_of_wealth"
            render={({ field }) => (
              <FormSelect
                label="Source of wealth"
                options={SOURCE_OF_WEALTH}
                {...field}
              />
            )}
          />
          <FormField
            control={form.control}
            name="pep_details"
            render={({ field }) => (
              <FormToggleGroup
                options={PEP_STATUS}
                {...field}
                label="Political exposure"
              />
            )}
          />
          <DialogFooter className="mt-auto flex sm:flex-col gap-4">
            <div className="flex items-center w-full gap-5">
              <Button variant="outline" type="button" onClick={previousStep}>
                Previous
              </Button>
              <Button
                className="w-full flex-1"
                type="submit"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting && (
                  <Loader2Icon className="size-4 animate-spin" />
                )}
                Next
              </Button>
            </div>
          </DialogFooter>
        </form>
      </Form>
    </div>
  );
}
